import { useState } from "react";
import { Buton, cn, Rozet } from "@koclukpanel/ui";
import type { Gorev } from "./api";
import { NEDENLER } from "./Yapamadim";
/* Sozlukler tek evde — gerekcesi orada; kopyasi iki dosyadaydi. */
import { DURUM, TUR_ADI } from "./gorev-etiketleri";
import { useCanliIzleme } from "./canli-izleme";

/** `MissReason` kodu -> ekrandaki etiket. Kaynak `Yapamadim`in listesi. */
const NEDEN_ETIKETI: Record<string, string> = Object.fromEntries(
  NEDENLER.map((n) => [n.kod, n.etiket]),
);

interface GorevKartiProps {
  gorev: Gorev;
  onVeriGir: () => void;
  onYapamadim: () => void;
  onSayac: () => void;
  sayacCalisiyor: boolean;
  bekleyenSenkron: boolean;
  /**
   * F-STV-04a — gecmis gunun gorevinde sayac YOK (sunucu da reddeder:
   * SAYAC_YALNIZ_BUGUN). Sure "Sonucu gir" formunda elle girilir.
   */
  sayacGizli?: boolean;
}

export function GorevKarti({
  gorev,
  onVeriGir,
  onYapamadim,
  onSayac,
  sayacCalisiyor,
  bekleyenSenkron,
  sayacGizli = false,
}: GorevKartiProps) {
  const [acik, setAcik] = useState(false);
  // F-LIV-07 — modul durumu uygulama duzeyinde BIR KEZ okunuyor (`canli-izleme.ts`); kart istek atmaz.
  const canli = useCanliIzleme();
  const d = DURUM[gorev.gosterilecekDurum] ?? DURUM.PLANNED!;
  const girildi = (gorev.correctCount ?? 0) + (gorev.wrongCount ?? 0) + (gorev.emptyCount ?? 0);

  return (
    <article
      className={cn(
        "rounded-kart bg-notr-0 shadow-kart border",
        gorev.isExcused ? "border-notr-200 opacity-60" : "border-notr-200",
      )}
    >
      <div className="flex items-start gap-3 p-4">
        <span
          className={cn(
            "flex size-9 shrink-0 items-center justify-center rounded-full text-base",
            d.sinif,
          )}
          // Ikon dekoratif DEGIL — durumu tasiyor. Metin karsiligi
          // asagida ayrica yaziyor, bu yuzden burada gizleniyor.
          aria-hidden="true"
        >
          {d.ikon}
        </span>

        <div className="min-w-0 flex-1">
          <h3 className="text-notr-900 text-base font-medium">
            {gorev.lesson.name}
            {gorev.topic && <span className="text-notr-600"> · {gorev.topic.name}</span>}
          </h3>

          <p className="text-notr-600 mt-0.5 text-sm">
            {TUR_ADI[gorev.kind] ?? gorev.kind}
            {gorev.targetQuestionCount !== null && ` · ${gorev.targetQuestionCount} soru`}
            {gorev.startTime && ` · ${gorev.startTime}`}
          </p>

          {/*
            A5 — KAYNAK KARTIN USTUNDE, AYRINTIDA DEGIL. Ogrenci masaya
            hangi kitabi alacagini acmadan gormeli; sayfa araligi kitapsiz
            anlamsiz ("s. 40-55" — hangi kitabin?).
          */}
          {gorev.kaynakAdi && (
            <p className="text-notr-700 mt-0.5 text-sm">Kaynak: {gorev.kaynakAdi}</p>
          )}

          <p className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
            <span className={cn("font-medium", d.sinif.split(" ").pop())}>{d.etiket}</span>
            {girildi > 0 && (
              <span className="text-notr-600">
                {gorev.correctCount ?? 0}D · {gorev.wrongCount ?? 0}Y · {gorev.emptyCount ?? 0}B
              </span>
            )}
            {gorev.isExcused && <span className="text-notr-600">· mazeretli</span>}
            {bekleyenSenkron && <Rozet>gönderilecek</Rozet>}
            {gorev.lateEnteredAt && <Rozet>sonradan girildi</Rozet>}
          </p>
        </div>

        <button
          type="button"
          onClick={() => setAcik((a) => !a)}
          aria-expanded={acik}
          /*
            ================================================================
            IKI OLCULEN KUSUR — 04.09.2026
            ================================================================
            1) UCGEN `notr-400` idi: beyaz zeminde 2,54. WCAG 1.4.11 metin
               OLMAYAN arayuz ogeleri icin 3,0 istiyor ve bu ucgen dugmenin
               tek gorsel isareti — yaninda gorunur bir etiket yok.
               `notr-500` ayni zeminde 4,83.

            2) DOKUNMA HEDEFI YOKTU. `px-2 py-1` ile dugme ~26px
               kaliyordu. Burasi OGRENCI uygulamasi: telefonda tek elle
               kullaniliyor ve 44px 03-kod-standartlari.md'de pazarlik
               disi. `size-dokunma` hem genisligi hem yuksekligi veriyor.
          */
          className={cn(
            "size-dokunma rounded-alan inline-flex shrink-0 items-center justify-center",
            "text-notr-600 hover:bg-notr-100 hover:text-notr-700",
          )}
        >
          <span className="sr-only">{acik ? "Ayrıntıyı kapat" : "Ayrıntıyı aç"}</span>
          <span aria-hidden="true">{acik ? "▴" : "▾"}</span>
        </button>
      </div>

      {acik && (
        <div className="border-notr-100 border-t px-4 py-3 text-sm">
          {gorev.coachNote && (
            <p className="rounded-alan bg-marka-50 text-marka-700 mb-3 px-3 py-2">
              {gorev.coachNote}
            </p>
          )}

          {/*
            ================================================================
            BILDIRDIGI MAZERET GERI GOSTERILIYOR
            ================================================================
            `missReason` `api.ts`te tanimliydi ve HIC cizilmiyordu;
            `missNote` tipte bile yoktu. Yani "Yapamadim" ekraninda
            neden secen — "Diger"de aciklama YAZMAK ZORUNDA olan —
            ogrenci, kocuna ne bildirdigini bir daha goremiyordu.
            Yanlis nedeni sectigini fark etse bile duzeltmesi icin
            once neyi sectigini gormesi gerekiyor.

            Kendi notu `bg-notr-50`de; koc notu yukarida marka
            zemininde. Ikisi ayri kutu, cunku biri ogrencinin sozu
            oteki kocun.
          */}
          {gorev.missReason && (
            <div className="rounded-alan bg-notr-50 mb-3 px-3 py-2">
              <p className="text-notr-600 font-medium">
                Bildirdiğin neden · {NEDEN_ETIKETI[gorev.missReason] ?? gorev.missReason}
              </p>
              {gorev.missNote && (
                <p className="text-notr-700 whitespace-pre-wrap">{gorev.missNote}</p>
              )}
            </div>
          )}
          {(gorev.pageFrom !== null || gorev.pageTo !== null) && (
            <p className="text-notr-600">
              Sayfa {gorev.pageFrom ?? "?"}–{gorev.pageTo ?? "?"}
            </p>
          )}
          {/*
            TEST ARALIGI DA YAZILIYOR. Uzman gorevi sayfa yerine test
            numarasiyla verebiliyor (`program.controller.ts` `testFrom`
            /`testTo`/`targetTestCount` kabul ediyor) ve sunucu bunlari
            gonderiyor; kart yalnizca sayfaya baktigi icin o gorevin
            ayrinti bolumu BOS aciliyordu — ogrenci hangi testi
            cozecegini gormuyordu.
          */}
          {(gorev.testFrom !== null || gorev.testTo !== null) && (
            <p className="text-notr-600">
              Test {gorev.testFrom ?? "?"}–{gorev.testTo ?? "?"}
            </p>
          )}
          {gorev.targetTestCount !== null && (
            <p className="text-notr-600">Hedef {gorev.targetTestCount} test</p>
          )}
          {/*
            ERTELENDIYSE HANGI GUNE. Rozet "Ertelendi" diyor ama tarihi
            sunucu gonderdigi halde yazilmiyordu; "ne zaman" sorusunun
            cevabi olmadan bu bilgi ogrenciye is gormuyor.
          */}
          {gorev.postponedToDate !== null && (
            <p className="text-notr-600">
              Ertelendiği gün:{" "}
              {new Date(gorev.postponedToDate).toLocaleDateString("tr-TR", {
                day: "numeric",
                month: "long",
                timeZone: "UTC",
              })}
            </p>
          )}
          {gorev.durationSeconds !== null && gorev.durationSeconds > 0 && (
            <p className="text-notr-600">
              Çalışma süresi: {Math.round(gorev.durationSeconds / 60)} dakika
            </p>
          )}

          {/* F-STV-08 — tek dokunusla acilir */}
          <div className="mt-3 flex flex-wrap gap-2">
            {gorev.topicPdfUrl && <BaglantiDugmesi url={gorev.topicPdfUrl} etiket="Konu PDF" />}
            {gorev.testPdfUrl && <BaglantiDugmesi url={gorev.testPdfUrl} etiket="Test PDF" />}
            {/* Uzman video adi girdiyse o yaziliyor: "Video" iki farkli
                videoyu ayirt etmiyor. */}
            {gorev.youtubeUrl && (
              <BaglantiDugmesi url={gorev.youtubeUrl} etiket={gorev.videoTitle ?? "Video"} />
            )}
          </div>
        </div>
      )}

      {!gorev.isExcused && (
        <div className="border-notr-100 flex gap-2 border-t p-3">
          <Buton boyut="genis" onClick={onVeriGir} className="flex-1">
            {girildi > 0 ? "Düzenle" : "Sonucu gir"}
          </Buton>
          {!sayacGizli && (
            <Buton
              gorunum={sayacCalisiyor ? "tehlike" : "ikincil"}
              onClick={onSayac}
              className="shrink-0"
            >
              {sayacCalisiyor ? "Bitir" : "Başlat"}
            </Buton>
          )}
          {/*
            GÖRÜNÜR SÖZCÜK, ✕ DEĞİL — UI/UX Dalga 1. Çarpı "kapat/sil"
            diye okunuyor: öğrenci görevi listeden atacağını sanıp ya hiç
            basmıyor ya da yanlışlıkla basıyordu. Bu düğme bir BEYAN
            ("yapamadım") ve ardından gerekçe istiyor — adı ekranda yazmalı.
          */}
          <Buton gorunum="sessiz" onClick={onYapamadim} className="shrink-0">
            Yapamadım
          </Buton>
        </div>
      )}

      {/*
        F-LIV-07 — SEFFAFLIK NOTU SAYACIN YANINDA. Modul acikken sayaci baslatan ogrenci, kocun bunu
        anlik gorebildigini baslatmadan ONCE bilmeli. Sayac gizliyse (gecmis gun) sayac yok, izleme de
        yok: not da gizli. Modul kapaliysa ya da durum okunamadiysa HIC cizilmez (`canli.acik` false).
      */}
      {!gorev.isExcused && !sayacGizli && canli.acik && (
        <div className="border-notr-100 flex items-center justify-between gap-2 border-t px-3 py-1">
          <p className="text-notr-600 text-xs">Koçun çalışma süreni anlık görebilir.</p>
          {canli.bilgiAc && (
            <button
              type="button"
              onClick={canli.bilgiAc}
              aria-label="Canlı çalışma izleme hakkında ayrıntı"
              className="min-h-dokunma text-marka-700 shrink-0 px-2 text-xs font-medium underline"
            >
              Ayrıntı
            </button>
          )}
        </div>
      )}
    </article>
  );
}

function BaglantiDugmesi({ url, etiket }: { url: string; etiket: string }) {
  return (
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      className="min-h-dokunma rounded-alan bg-notr-100 text-notr-700 hover:bg-notr-200 inline-flex items-center px-3 text-sm font-medium"
    >
      {etiket}
    </a>
  );
}
